import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, ArrowLeft, CheckCircle2, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function VerifyOTP() {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const resend_token = location.state?.resend_token;
  const inputRefs = useRef([]);

  useEffect(() => {
    // If no email or resend_token in state, redirect back to forgot-password
    if (!email || !resend_token) {
      navigate('/forgot-password');
    }
  }, [email, resend_token, navigate]);

  const handleChange = (index, value) => {
    if (isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Backspace logic
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/request-otp`, { email });
      // Update the resend_token in state if it changed
      navigate('/verify-otp', { state: { email, resend_token: data.resend_token }, replace: true });
      setError('A new OTP has been sent to your email.');
      setTimeout(() => setError(''), 5000);
    } catch (err) {
      setError('Failed to resend OTP.');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const { data } = await axios.post(`${API_URL}/api/auth/verify-otp`, { 
        email, 
        otp: otpCode,
        resend_token 
      });
      // Redirect to reset password with email and resetToken in state
      navigate('/reset-password', { state: { email, resetToken: data.reset_token } });
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center p-6 overflow-hidden bg-[#0A0C10]">
      {/* Background Aura Effects */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-[#D0021B]/10 blur-[120px]"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-600/10 blur-[120px]"></div>
      
      <motion.div 
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative z-10 w-full max-w-[450px]"
      >
        <div className="group relative bg-white/[0.03] backdrop-blur-2xl border border-white/[0.08] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] rounded-[2.5rem] overflow-hidden">
          <div className="px-8 pt-12 pb-10">
            <div className="text-center mb-10">
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="inline-flex p-4 rounded-3xl bg-[#D0021B]/10 border border-[#D0021B]/20 mb-6"
              >
                <ShieldCheck className="w-8 h-8 text-[#D0021B]" />
              </motion.div>
              
              <h1 className="font-serif text-3xl font-bold text-white tracking-tight mb-2 uppercase">
                Verify OTP
              </h1>
              <p className="text-white/40 text-sm tracking-wide uppercase font-bold">
                Sent to: <span className="text-white/80">{email}</span>
              </p>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 overflow-hidden"
                >
                  <div className={`p-4 border rounded-2xl text-sm flex items-center gap-3 ${
                    error.includes('sent') ? 'bg-green-500/10 border-green-500/20 text-green-200' : 'bg-red-500/10 border-red-500/20 text-red-200'
                  }`}>
                    {error.includes('sent') ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
                    {error}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="flex justify-between gap-2">
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => (inputRefs.current[idx] = el)}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleChange(idx, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(idx, e)}
                    className="w-12 h-14 bg-white/[0.03] border border-white/10 focus:border-[#D0021B]/40 rounded-xl text-white text-xl font-bold text-center focus:bg-white/[0.05] focus:ring-4 focus:ring-[#D0021B]/5 transition-all outline-none"
                    required
                  />
                ))}
              </div>

              <motion.button
                whileHover={{ scale: 1.01, y: -2 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="relative w-full py-4 bg-[#D0021B] text-white font-bold rounded-2xl shadow-[0_8px_32px_rgba(208,2,27,0.3)] hover:shadow-[0_12px_48px_rgba(208,2,27,0.45)] transition-all disabled:opacity-50 group overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                {loading ? 'VERIFYING...' : 'VERIFY CODE'}
              </motion.button>
            </form>

            <div className="mt-8 flex flex-col items-center gap-4">
              <button 
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="text-sm font-bold text-white/40 hover:text-[#D0021B] transition-colors flex items-center gap-2 uppercase tracking-widest disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
                {resending ? 'Resending...' : 'Resend Code'}
              </button>

              <Link to="/forgot-password" className="inline-flex items-center gap-2 text-white/40 hover:text-white transition-colors text-xs uppercase tracking-widest group">
                <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
                Change Email
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
